const CLIENT_ID = import.meta.env.VITE_SPOTIFY_CLIENT_ID;
const CLIENT_SECRET = import.meta.env.VITE_SPOTIFY_CLIENT_SECRET;
const REDIRECT_URI = import.meta.env.VITE_REDIRECT_URI;

class SpotifyAuthService {
  constructor() {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpirationTime = null;
    this.loadTokensFromStorage();
  }

  loadTokensFromStorage() {
    try {
      this.accessToken = localStorage.getItem('spotify_access_token');
      this.refreshToken = localStorage.getItem('spotify_refresh_token');
      this.tokenExpirationTime = localStorage.getItem('spotify_token_expiration');
    } catch (error) {
      console.error('Error loading tokens:', error);
    }
  }

  generateRandomString(length) {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const values = crypto.getRandomValues(new Uint8Array(length));
    return values.reduce((acc, x) => acc + possible[x % possible.length], "");
  }

  getLoginUrl() {
    const state = this.generateRandomString(16);
    localStorage.setItem('spotify_auth_state', state);

    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      response_type: 'code',
      redirect_uri: REDIRECT_URI,
      state: state,
      scope: [
        'user-read-private',
        'user-read-email',
        'playlist-read-private',
        'playlist-read-collaborative',
        'playlist-modify-public',
        'playlist-modify-private'
      ].join(' '),
      show_dialog: true
    });

    console.log('Generated login URL with params:', {
      clientId: CLIENT_ID,
      redirectUri: REDIRECT_URI,
      state: state
    });

    return `https://accounts.spotify.com/authorize?${params.toString()}`;
  }

  async handleCallback(code, state) {
    const storedState = localStorage.getItem('spotify_auth_state');
    console.log('Handling callback:', { code, state, storedState, redirectUri: REDIRECT_URI });
    
    if (state !== storedState) {
      throw new Error('State mismatch');
    }

    try {
      const params = new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        redirect_uri: REDIRECT_URI
      });

      const authHeader = btoa(`${CLIENT_ID}:${CLIENT_SECRET}`);
      console.log('Auth request details:', {
        url: 'https://accounts.spotify.com/api/token',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Basic ${authHeader}`
        },
        body: params.toString()
      });

      const response = await fetch('https://accounts.spotify.com/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Basic ${authHeader}`
        },
        body: params
      });

      const responseText = await response.text();
      console.log('Token response:', {
        status: response.status,
        statusText: response.statusText,
        body: responseText
      });

      if (!response.ok) {
        throw new Error(`Token request failed: ${responseText}`);
      }

      const data = JSON.parse(responseText);
      
      this.accessToken = data.access_token;
      if (data.refresh_token) {
        this.refreshToken = data.refresh_token;
        localStorage.setItem('spotify_refresh_token', data.refresh_token);
      }
      
      this.tokenExpirationTime = Date.now() + (data.expires_in * 1000);
      localStorage.setItem('spotify_access_token', this.accessToken);
      localStorage.setItem('spotify_token_expiration', this.tokenExpirationTime);

      return this.accessToken;
    } finally {
      localStorage.removeItem('spotify_auth_state');
    }
  }

  async getAccessToken() {
    if (this.isTokenExpired()) {
      if (this.refreshToken) {
        await this.refreshAccessToken();
      } else {
        return null;
      }
    }
    return this.accessToken;
  }

  isTokenExpired() {
    return !this.tokenExpirationTime || Date.now() >= parseInt(this.tokenExpirationTime);
  }

  async refreshAccessToken() {
    try {
      const response = await fetch('https://accounts.spotify.com/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': 'Basic ' + btoa(`${CLIENT_ID}:${CLIENT_SECRET}`)
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: this.refreshToken
        })
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      this.accessToken = data.access_token;
      this.tokenExpirationTime = Date.now() + (data.expires_in * 1000);
      
      localStorage.setItem('spotify_access_token', this.accessToken);
      localStorage.setItem('spotify_token_expiration', this.tokenExpirationTime);
      
      return this.accessToken;
    } catch (error) {
      this.logout();
      throw error;
    }
  }

  logout() {
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_refresh_token');
    localStorage.removeItem('spotify_token_expiration');
    localStorage.removeItem('spotify_auth_state');
    
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpirationTime = null;
  }

  isAuthenticated() {
    return !!this.accessToken && !this.isTokenExpired();
  }
}

export default new SpotifyAuthService();